<template>
  <div class="chart-container">
    <canvas ref="chartCanvas"></canvas>
  </div>
</template>

<script setup>
import { ref, onMounted, watch } from 'vue'
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
} from 'chart.js'

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
)

const props = defineProps({
  period: {
    type: String,
    default: 'month',
    validator: value => ['week', 'month', 'quarter'].includes(value)
  }
})

const chartCanvas = ref(null)
let chartInstance = null

const chartData = ref({
  week: {
    labels: ['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'],
    datasets: [
      {
        label: 'Développeur Frontend',
        data: [12, 15, 8, 22, 18, 5, 3],
        borderColor: '#1976d2',
        backgroundColor: 'rgba(25, 118, 210, 0.1)',
        fill: true,
        tension: 0.4
      },
      {
        label: 'Développeur Backend',
        data: [8, 12, 15, 18, 14, 2, 1],
        borderColor: '#388e3c',
        backgroundColor: 'rgba(56, 142, 60, 0.1)',
        fill: true,
        tension: 0.4
      },
      {
        label: 'Designer UX/UI',
        data: [5, 8, 12, 15, 10, 3, 2],
        borderColor: '#f57c00',
        backgroundColor: 'rgba(245, 124, 0, 0.1)',
        fill: true,
        tension: 0.4
      }
    ]
  },
  month: {
    labels: ['Sem 1', 'Sem 2', 'Sem 3', 'Sem 4'],
    datasets: [
      {
        label: 'Développeur Frontend',
        data: [65, 78, 85, 92],
        borderColor: '#1976d2',
        backgroundColor: 'rgba(25, 118, 210, 0.1)',
        fill: true,
        tension: 0.4
      },
      {
        label: 'Développeur Backend',
        data: [48, 62, 58, 74],
        borderColor: '#388e3c',
        backgroundColor: 'rgba(56, 142, 60, 0.1)',
        fill: true,
        tension: 0.4
      },
      {
        label: 'Designer UX/UI',
        data: [32, 45, 52, 48],
        borderColor: '#f57c00',
        backgroundColor: 'rgba(245, 124, 0, 0.1)',
        fill: true,
        tension: 0.4
      }
    ]
  },
  quarter: {
    labels: ['Jan', 'Fév', 'Mar', 'Avr', 'Mai', 'Jun', 'Jul', 'Aoû', 'Sep', 'Oct', 'Nov', 'Déc'],
    datasets: [
      {
        label: 'Développeur Frontend',
        data: [245, 268, 298, 315, 342, 358, 385, 395, 412, 428, 445, 465],
        borderColor: '#1976d2',
        backgroundColor: 'rgba(25, 118, 210, 0.1)',
        fill: true,
        tension: 0.4
      },
      {
        label: 'Développeur Backend',
        data: [185, 198, 225, 248, 265, 285, 298, 315, 328, 345, 362, 378],
        borderColor: '#388e3c',
        backgroundColor: 'rgba(56, 142, 60, 0.1)',
        fill: true,
        tension: 0.4
      },
      {
        label: 'Designer UX/UI',
        data: [125, 142, 158, 175, 185, 198, 212, 225, 238, 248, 265, 278],
        borderColor: '#f57c00',
        backgroundColor: 'rgba(245, 124, 0, 0.1)',
        fill: true,
        tension: 0.4
      }
    ]
  }
})

const chartOptions = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: {
      position: 'bottom',
      labels: {
        usePointStyle: true,
        padding: 20
      }
    },
    tooltip: {
      mode: 'index',
      intersect: false,
      backgroundColor: 'rgba(0, 0, 0, 0.8)',
      titleColor: '#fff',
      bodyColor: '#fff',
      borderColor: '#fff',
      borderWidth: 1
    }
  },
  scales: {
    x: {
      grid: {
        display: false
      },
      ticks: {
        color: '#666'
      }
    },
    y: {
      beginAtZero: true,
      grid: {
        color: 'rgba(0, 0, 0, 0.1)'
      },
      ticks: {
        color: '#666'
      }
    }
  },
  interaction: {
    mode: 'nearest',
    axis: 'x',
    intersect: false
  }
}

const createChart = () => {
  if (chartInstance) {
    chartInstance.destroy()
  }
  
  const ctx = chartCanvas.value.getContext('2d')
  chartInstance = new ChartJS(ctx, {
    type: 'line',
    data: chartData.value[props.period],
    options: chartOptions
  })
}

const updateChart = () => {
  if (chartInstance) {
    chartInstance.data = chartData.value[props.period]
    chartInstance.update('active')
  }
}

watch(() => props.period, updateChart)

onMounted(() => {
  createChart()
})
</script>

<style scoped>
.chart-container {
  position: relative;
  height: 300px;
  width: 100%;
}
</style>
